import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { TYPES } from '@/engine/ioc/types';
import { useInjection } from 'inversify-react';
import React, { useEffect, useMemo } from 'react';
import type * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { debounce, throttle } from 'underscore';
import { NumberTuner } from '@/engine/PlanningAlgorithm/Tuners/NumberTuner';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ICommandProcessor, ICommand } from '@/engine/app/modules/core/interfaces/Command';
import type { ITuneCommandFactory } from '@/engine/PlanningAlgorithm/Tuners/commands/interfaces';
import type { IViewItemsViewModelController } from '@/engine/app/interfaces';
import { AnonymousCommand } from '@/engine/app/modules/core/commands/Anonymous.command';
import { CompositeCommand } from '@/engine/app/modules/core/commands/Composite.command';

export interface INumberTuner {
  doTune: (value: number) => void;
}

export interface IUndoSaver {
  saveUndoValue: () => void;
}

export const useThrottledNumberTuner = (
  descriptor: string,
  waitMs: number,
  locatableDescriptors?: Array<string>
): {
  value: number;
  range: [number, number];
  tuner: INumberTuner;
  finalizer: INumberTuner;
  undoValueSaver: IUndoSaver;
} => {
  const viVmController = useInjection<IViewItemsViewModelController>(TYPES.IViewItemsViewModelController);
  const planningAlgorithm = useInjection<PlanningAlgorithm>(TYPES.PlanningAlgorithm);
  const tuneCommandsFactory = useInjection<ITuneCommandFactory<ICommand, NumberTuner, number>>(
    TYPES.NumberTuneCommandsFactory
  );
  const commandProcessor = useInjection<ICommandProcessor>(CORE_TYPES.CommandProcessor);
  const algoTuner = useMemo(() => new NumberTuner(planningAlgorithm), [planningAlgorithm]);
  const [value, setValue] = React.useState(0);
  const [undoValue, setUndoValue] = React.useState(0);
  const [range, setRange] = React.useState<[number, number]>([0, 0]);
  const [tuner, setTuner] = React.useState<INumberTuner>({
    doTune: (): void => {
      return;
    }
  });

  const [undoSaver, setUndoSaver] = React.useState<IUndoSaver>({
    saveUndoValue: (): void => {
      return;
    }
  });
  const [finalizer, setFinalizer] = React.useState<INumberTuner>({
    doTune: (): void => {
      return;
    }
  });
  const [isSelfTune, setIsSelfTune] = React.useState<boolean>(false);

  useEffect(() => {
    const _valueSetter = async (val: number): Promise<void> => {
      await algoTuner.setValueAsync(descriptor, val);
      if (locatableDescriptors) {
        for (const locatableDescriptor of locatableDescriptors) {
          if (viVmController.getActualViewModels().find((vm) => vm.descriptor === locatableDescriptor)?.isSelected) {
            viVmController.locateMeasurementByDescriptor(locatableDescriptor);
            break;
          }
        }
      }
    };

    const _onValueChanged = (tuneValue?: DT.NumberT): void => {
      if (!tuneValue) {
        throw new Error('tuneValue expected to be valid here');
      }
      setIsSelfTune(false);
      setValue(tuneValue.value);
    };

    const valueSetter = throttle(_valueSetter, waitMs);

    const onValueChanged = debounce(_onValueChanged, waitMs + 200);

    const onRangeChanged = (tuneValue?: DT.RangeT): void => {
      if (!tuneValue) {
        throw new Error('tuneValue expected to be valid here');
      }
      setRange(tuneValue.value);
    };

    const subscriptions: DisposablesBasket = new DisposablesBasket();
    subscriptions.add(planningAlgorithm.addOnChangedCallbackFor<DT.NumberT>(descriptor, onValueChanged));
    subscriptions.add(
      planningAlgorithm.addOnChangedCallbackFor<DT.RangeT>(algoTuner.getRangeName(descriptor), onRangeChanged)
    );

    setValue(algoTuner.getValue(descriptor));
    setRange(algoTuner.getValuesRange(descriptor));
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
    setTuner({ doTune: valueSetter });

    return (): void => subscriptions.dispose();
  }, [planningAlgorithm, descriptor, waitMs, algoTuner, viVmController, locatableDescriptors]);

  useEffect(() => {
    const _undoValueSaver = (): void => {
      setUndoValue(algoTuner.getValue(descriptor));
    };
    setUndoSaver({ saveUndoValue: _undoValueSaver });
  }, [algoTuner, descriptor]);

  useEffect(() => {
    const _undoRedoFinalizer = (val: number): void => {
      const tuneCommand = tuneCommandsFactory.createCommand(algoTuner, descriptor, val, undoValue);
      let foundLocatableDescriptor: string | undefined = undefined;
      if (locatableDescriptors) {
        for (const locatableDescriptor of locatableDescriptors) {
          if (viVmController.getActualViewModels().find((vm) => vm.descriptor === locatableDescriptor)?.isSelected) {
            foundLocatableDescriptor = locatableDescriptor;
            break;
          }
        }
      }
      if (foundLocatableDescriptor) {
        const locateFunc = (): Promise<boolean | undefined> => {
          viVmController.locateMeasurementByDescriptor(foundLocatableDescriptor);
          return Promise.resolve(true);
        };
        const locateCommand = new AnonymousCommand(locateFunc, locateFunc);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
        commandProcessor.execute(new CompositeCommand(tuneCommand, locateCommand));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
        commandProcessor.execute(tuneCommand);
      }
    };
    setFinalizer({ doTune: _undoRedoFinalizer });
  }, [algoTuner, descriptor, tuneCommandsFactory, commandProcessor, undoValue, viVmController, locatableDescriptors]);

  useEffect(() => {
    if (isSelfTune) {
      tuner?.doTune(value);
    }
  }, [tuner, value, isSelfTune]);

  const tunerAdapter = {
    doTune: (value: number): void => {
      setIsSelfTune(true);
      setValue(value);
    }
  };

  return { value: value, range: range, tuner: tunerAdapter, finalizer: finalizer, undoValueSaver: undoSaver };
};
